import { useCallback, useEffect, useRef, useState } from "react";
import { useSubmission, type SubmissionState } from "../../shared/state/useSubmission";
import { useUnsavedDraft } from "../processAuthoring/draft/useUnsavedDraft";
import { stagedEdit, withoutStaged, withStaged, type StagedEdit, type StagedEdits } from "./stagedEdits";

export interface EditSession<Edit> {
  /** The edits made in place and not yet saved, in the order they were made. */
  readonly edits: StagedEdits<Edit>;
  readonly stagedOf: (key: string) => StagedEdit<Edit> | undefined;
  /** Make `edit` the unsaved edit of `key`; with a problem, it blocks the save until fixed. */
  readonly stage: (key: string, edit: Edit, problem?: string | null) => void;
  readonly unstage: (key: string) => void;
  /** The one thing being edited, if any. */
  readonly openKey: string | null;
  /** Edit `key`; Cancel goes back to its edit as it is now. */
  readonly open: (key: string) => void;
  /** Stop editing, keeping what was typed. */
  readonly close: () => void;
  /** Stop editing and go back to the edit there was when it opened. */
  readonly cancel: () => void;
  /** Drop every unsaved edit, the open one too. */
  readonly discard: () => void;
  /** Whether an edit has a problem: Save waits until it is fixed or dropped. */
  readonly blocked: boolean;
  /** Save every edit through the owner's write; they leave the session once it succeeded. */
  readonly save: () => void;
  /** Close this editor: with unsaved edits here, the draft guard asks first, about these edits only. */
  readonly requestClose: (close: () => void) => void;
  readonly saving: SubmissionState;
}

interface Opened<Edit> {
  readonly key: string;
  /** What Cancel restores: the key's edit when it was opened, or none. */
  readonly before: StagedEdit<Edit> | undefined;
}

/**
 * Edit in place, then one explicit Save or Discard (F96, as v1's annotation review held its
 * edits until Accept). The session owns the edits, including the one being typed, and which
 * one is open, so Save, Discard and the leave guard always see all of them. `write` is the
 * owner's own write path, such as a term's change set proposal, so nothing writes beside it.
 * While edits wait, leaving the page asks first (`UnsavedDraftGuard`), and its "Save" saves them.
 */
export function useEditSession<Edit>(write: (edits: readonly Edit[]) => Promise<void>): EditSession<Edit> {
  const [edits, setEdits] = useState<StagedEdits<Edit>>([]);
  const [opened, setOpened] = useState<Opened<Edit> | null>(null);
  const { state: saving, submit, run, reset } = useSubmission();
  const { connectSaver, requestClose } = useUnsavedDraft(edits);
  const blocked = edits.some((staged) => staged.problem !== null);
  // The draft guard's Save and a save's write read the edits and write of the last render.
  const latest = useRef({ edits, write });
  useEffect(() => {
    latest.current = { edits, write };
  });

  /** Write `saved`; edits made meanwhile stay for the next save. */
  const writeEdits = useCallback(async (saved: StagedEdits<Edit>) => {
    await latest.current.write(saved.map((staged) => staged.edit));
    setEdits((now) => now.filter((staged) => !saved.includes(staged)));
    // An open edit that was saved closes: Cancel must not bring back what came before it (Sol R1).
    setOpened((now) => (now !== null && saved.some((staged) => staged.key === now.key) ? null : now));
  }, []);

  useEffect(() => {
    connectSaver({
      save: () => {
        const current = latest.current.edits;
        // Through the one submission, so it never writes beside a Save that is still running (Sol S5).
        return current.some((staged) => staged.problem !== null) ? Promise.resolve(false) : run(() => writeEdits(current));
      },
    });
    return () => { connectSaver(null); };
  }, [connectSaver, writeEdits, run]);

  const changed = (next: (current: StagedEdits<Edit>) => StagedEdits<Edit>) => {
    if (saving.status === "failed") reset();
    setEdits(next);
  };
  return {
    edits,
    stagedOf: (key) => stagedEdit(edits, key),
    stage: (key, edit, problem = null) => { changed((current) => withStaged(current, { key, edit, problem })); },
    unstage: (key) => { changed((current) => withoutStaged(current, key)); },
    openKey: opened?.key ?? null,
    open: (key) => { setOpened({ key, before: stagedEdit(edits, key) }); },
    close: () => { setOpened(null); },
    cancel: () => {
      if (opened !== null) {
        const { key, before } = opened;
        changed((current) => (before === undefined ? withoutStaged(current, key) : withStaged(current, before)));
      }
      setOpened(null);
    },
    discard: () => {
      setOpened(null);
      changed(() => []);
    },
    blocked,
    save: () => { if (edits.length > 0 && !blocked) submit(() => writeEdits(edits)); },
    requestClose: (close) => { requestClose(close); },
    saving,
  };
}
